import { SampleLifecycle } from '@texas/components/shared/samples/types';
import { EntityHistory, SampleHistoryEvent } from '../historyTypes';
import { httpClient } from '../httpClient/httpClient';
import { SimpleProductApproval } from './productApprovalsApi';
import { ReceiverUser, ShipmentState } from './shipmentsApi';
import { SlimTag } from './tagsApi';
import { EntityType, Trackable } from './trackerApi';
import { SlimUser } from './userApi';

export enum SampleState {
  Active = 1,
  Lost = 2,
  Inactive = 3,
}

export enum SampleReceiver {
  ProductDeveloper = 1,
  BusinessCoordinator = 2,
  Brand = 3,
}

export enum SampleShipmentState {
  Unpacked = 2,
  Lost = 3,
  InTransportRequest = 4,
}

interface TransportShipment {
  id: number;
  dispatchDate: string;
  state: ShipmentState;
  courier: string;
  trackingNumber: string;
}

export interface SampleTransportRequest {
  id: number;
  requestedDispatchDate: string;
  receiver: SampleReceiver;
  receiverUser: ReceiverUser;
  shipment: TransportShipment | null;
  shipmentState: SampleShipmentState;
}

export interface BaseSample extends Trackable {
  articleName: string;
  articleId: number;
  customerId: number;
  customerName: string;
  articleFolderId: number;
  articleItemCode: string;
  variantId: number;
  branchId: number;
  branchName: string;
  supplierId: number;
  supplierName: string;
  version: number;
  option: number;
  compositionGroupId: number;
  compositionGroupArchived: boolean;
  sampleId: number;
  matrixNodeXValue: string | null;
  matrixNodeYValue: string | null;
  inPossessionOfUser: SlimUser;
  sampleReadyDate: string | null;
  sampleInitiatedDate: string;
  receivedFromSupplierUnknownShipment: string | null;
  isRejected: boolean;
  state: SampleState;
  productApproval: SimpleProductApproval;
  lifecycle: SampleLifecycle;
  tags: Record<EntityType, SlimTag[] | undefined>;
}

export interface Sample extends BaseSample {
  transport: SampleTransportRequest | null;
}

export enum RejectSampleReason {
  Size = 1,
  Color = 2,
  Quality = 3,
}

export interface RejectSampleRequest {
  reasons: RejectSampleReason[];
  note: string;
  photos: { note: string; fileLinkId: number }[];
  addToTodo: boolean;
}

export interface SampleHistories {
  id: number;
  histories: EntityHistory<SampleHistoryEvent>[];
}

export interface SamplesQuery {
  sortBy: string;
  sortDesc: boolean;
}

export interface MarkReceivedSamplesRequest {
  samples: { sampleId: number; supplierItemCode: string }[];
}

export interface UpdateReadyDateRequest {
  sampleIds: number[];
  readyDate: Date;
}

export const samplesApi = {
  fromIds: (sampleIds: number[]) =>
    httpClient.get<Sample[]>(`samples/ids`, {
      params: { sampleIds },
    }),
  history: (sampleId: number) =>
    httpClient.get<SampleHistories>(`samples/${sampleId}/history`),
  setAsUnknownShipmentReceived: (request: MarkReceivedSamplesRequest) =>
    httpClient.put(`samples/received/unknown`, {
      data: { samples: request.samples },
    }),
  markLost: (sampleIds: number[]) =>
    httpClient.put(`samples/lost`, {
      data: { sampleIds },
    }),
  updateOwner: (sampleIds: number[], userId: number) =>
    httpClient.put(`samples/update-owner`, {
      data: { sampleIds, userId },
    }),
  updateReadyDate: (request: UpdateReadyDateRequest) =>
    httpClient.put(`samples/ready-date`, {
      data: request,
    }),
};
